import React, {Fragment} from 'react';

import {EditableChoiceField} from 'app/procedures/components/EditableChoiceField';
import {StepDecisionTitle} from 'app/procedures/components/structures';
import {addNewProcedureToProcessVersionContent} from 'app/process/actions';
import {SetStepLaneButton} from 'app/steps/components/edit/SetStepLaneButton';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';

import {EditableProcedureStepFiles} from './EditableProcedureStepFiles';

import type {AttachedChoiceType} from 'app/general/types';
import type {
  ApplyStepChoicesFuncType,
  ApplyStepShaperFuncType,
} from 'app/steps/document-shaper';
import type {
  ProcedureDecisionStepType,
  ProcedureEndingStepType,
  ProcedureStepType,
  ProcedureVersionSerializedType,
  ProcessContentType,
  ProcessProcedureAsStepSerializedType,
  ProcessVersionSerializedType,
} from 'sweet-types';

type Props = {
  url: string;
  step: ProcedureStepType;
  choices: Array<AttachedChoiceType>;
  steps: Array<ProcedureStepType>;
  applyStepChoicesShaper: ApplyStepChoicesFuncType;
  applyStepShaper: ApplyStepShaperFuncType;
  isProcess?: boolean;
  stepLaneId?: string | null;
  onAddProcedure?: (version: ProcedureVersionSerializedType) => unknown;
};

export function EditableProcedureDecisionFooter({
  url,
  step,
  choices,
  steps,
  applyStepChoicesShaper,
  applyStepShaper,
  isProcess,
  stepLaneId,
  onAddProcedure,
}: Props) {
  const actions = useAsyncActions({addNewProcedureToProcessVersionContent});
  return (
    <Fragment>
      {choices.length > 0 ? (
        <div className="mb-4">
          <StepDecisionTitle className="mt-3" />
          {choices.map((choice, index) => (
            <EditableChoiceField
              key={choice.id}
              choice={choice}
              steps={steps}
              index={index}
              numChoices={choices.length}
              onSetDestination={(targetId: any) =>
                applyStepChoicesShaper((choicesShaper) =>
                  choicesShaper.setTarget(choice.id, targetId).procedure()
                )
              }
              createAndSetDestination={(title: string) => {
                if (!isProcess) {
                  return applyStepChoicesShaper((choicesShaper) =>
                    choicesShaper
                      .setTarget(choice.id, {
                        type: 'newStep',
                        title,
                      })
                      .procedure()
                  );
                } else {
                  return actions
                    .addNewProcedureToProcessVersionContent(url, title, step.id)
                    .then(onAddProcedure)
                    .then((version: ProcessVersionSerializedType) => {
                      const newStep = getNextStep(version.content, step.id);
                      return applyStepChoicesShaper((choicesShaper) =>
                        choicesShaper
                          // on error default to current step (?? step.id)
                          .setTarget(choice.id, newStep?.id ?? step.id)
                          .procedure()
                      );
                    });
                }
              }}
              onRemove={(toRemove) =>
                applyStepChoicesShaper((shaper) =>
                  shaper.remove(toRemove.id).procedure()
                )
              }
              onChange={(choice) =>
                applyStepChoicesShaper((shaper) =>
                  shaper.update(choice.id, choice).procedure()
                )
              }
              onReorder={(source, target) =>
                applyStepChoicesShaper((choicesShaper) =>
                  choicesShaper.move(source, target).procedure()
                )
              }
            />
          ))}
        </div>
      ) : null}
      <div className="text-center mb-4">
        <button
          onClick={() =>
            applyStepChoicesShaper((stepChoicesShaper) =>
              stepChoicesShaper.addBlank().procedure()
            )
          }
          className="btn btn-sm btn-primary"
        >
          {gt('Add new Answer')}
        </button>
      </div>
      <div className="d-flex flex-wrap justify-content-end mb-2 pt-2 border-top">
        <div className="mr-1">
          <SetStepLaneButton
            applyStepShaper={applyStepShaper}
            stepLaneId={stepLaneId}
          />
        </div>
      </div>
      {step.type === 'content' && step.files && step.files.length ? (
        <div className="step-files">
          <EditableProcedureStepFiles
            files={step.files}
            onRemove={(index) =>
              applyStepShaper((stepShaper) =>
                stepShaper.files.removeByIndex(index).procedure()
              )
            }
          />
        </div>
      ) : null}
    </Fragment>
  );
}

function getNextStep(
  content: ProcessContentType,
  currentId: string
):
  | ProcessProcedureAsStepSerializedType
  | ProcedureDecisionStepType
  | ProcedureEndingStepType
  | undefined {
  const currentIndex = content.findIndex((step) => step.id === currentId);

  // Check if the current step is found and it's not the last one in the array
  if (currentIndex >= 0 && currentIndex < content.length - 1) {
    return content[currentIndex + 1];
  }

  // Return undefined if the current step is the last one or not found
  return undefined;
}
